import { z } from "zod";

import type {
  ConfigurationField,
  ConfigurationFormValue,
  ConfigurationNestedValue,
} from "../types";

type ConfigurationFieldSchema = z.ZodType<
  ConfigurationFormValue,
  ConfigurationFormValue
>;

type ConfigurationNestedSchema = z.ZodType<
  ConfigurationNestedValue,
  ConfigurationNestedValue
>;

/**
 * Recursively builds a Zod schema for a single field
 */
function buildFieldSchema(
  field: ConfigurationField,
  isEnabled: boolean,
  translate?: (key: string, options?: Record<string, string>) => string
): ConfigurationFieldSchema {
  const isRequired = field.required && isEnabled;

  const getTranslatedLabel = () => {
    if (!field.label) return "";
    if (translate && field.label.startsWith("integrations.")) {
      return translate(field.label);
    }
    return field.label;
  };

  const translatedLabel = getTranslatedLabel();
  const requiredMessage = translate
    ? translate("errors.requiredField", { fieldName: translatedLabel })
    : `${translatedLabel} is required when enabling the module`;

  switch (field.type) {
  case "email":
    if (isRequired) {
      return z
        .string()
        .min(1, requiredMessage)
        .email("Invalid email address");
    }
    return z
      .string()
      .email("Invalid email address")
      .or(z.literal(""))
      .optional();

  case "number":
    if (isRequired) {
      return z
        .string()
        .min(1, requiredMessage)
        .regex(/^\d+$/, "Must be a valid number");
    }
    return z.string().regex(/^\d*$/, "Must be a valid number").optional();

  case "boolean":
    return z
      .string()
      .refine(
        (val) => val === "true" || val === "false" || val === "",
        "Must be true or false"
      )
      .optional();

  case "multi-input-list": {
    const nestedFields = field.options?.fields || [];
    const nestedValuesShape: Record<string, ConfigurationNestedSchema> = {};

    nestedFields.forEach((nestedField) => {
      nestedValuesShape[nestedField.key] = buildFieldSchema(
        nestedField,
        isEnabled,
        translate
      );
    });

    // Index is always required
    nestedValuesShape.Index = z.number();

    const listItemSchema = z.object({
      id: z.string(),
      values: z.object(nestedValuesShape),
    });

    if (isRequired) {
      return z
        .array(listItemSchema)
        .min(
          1,
          `${field.label} must have at least one item when enabling the module`
        );
    }
    return z.array(listItemSchema).optional();
  }

  case "reference":
  case "reference-list":
  case "password":
  case "text":
  case "select":
  default:
    if (isRequired) {
      return z.string().min(1, requiredMessage);
    }
    return z.string().optional();
  }
}

/**
 * Builds a Zod validation schema from configuration field definitions
 * Handles required/optional fields and type-specific validation
 * Fields are only required when the module is being enabled
 */
export function buildConfigurationSchema(
  fields: ConfigurationField[],
  isEnabled: boolean,
  translate?: (key: string, options?: Record<string, string>) => string
): z.ZodObject<Record<string, ConfigurationFieldSchema>> {
  const shape: Record<string, ConfigurationFieldSchema> = {};

  fields.forEach((field) => {
    shape[field.key] = buildFieldSchema(field, isEnabled, translate);
  });

  return z.object(shape);
}
